<template>
  <ExtracadabraModal
    v-model:opened="openedInternal"
    :header="{
      img: 'interrogation_mark.svg',
      bgColor: 'primary',
      textColor: 'white',
    }"
    :body="{ class: 'text-center' }"
  >
    <template #body>
      <h3 class="text-center">
        {{ $t("ETAB.PAGES.RECRUITMENT.REPORT_CANDIDATE_MODAL.@TITLE", { name: candidateFirstname }) }}
      </h3>
      <VeeForm ref="form" @submit="save">
        <QCardSection class="modal-input">
          <fieldset>
            <label class="form-label">
              {{ $t("ETAB.FORMS.LABELS.@REPORT_CANDIDATE_WARNING") }}
            </label>
            <Field
              v-slot="{ errors, field, value }"
              v-model="reasonId"
              class="q-my-sm"
              tag="div"
              :name="$filters.formLabelValidation($t('ETAB.FORMS.LABELS.@REPORT_CANDIDATE_REASONS'))"
              :rules="{ required: true }"
            >
              <QField
                v-bind="field"
                :model-value="value"
                :value="reasonId"
                borderless
                class="candidate-multiple-choice-field"
                no-error-icon
                :error="errors.length > 0"
                :error-message="errors[0]"
              >
                <QOptionGroup v-model="reasonId" :options="formattedReportCandidateReasons" color="primary" />
              </QField>
            </Field>
            <Field
              v-slot="{ errors, field, value }"
              v-model="comment"
              class="q-my-sm"
              tag="div"
              :name="$filters.formLabelValidation($t('ETAB.FORMS.LABELS.@COMMENT'))"
              :rules="{ required: isCommentMandatory }"
            >
              <QInput
                v-bind="field"
                :model-value="value"
                filled
                stack-label
                :label="$t('ETAB.FORMS.LABELS.@COMMENT')"
                type="textarea"
                counter
                :maxlength="MAX_LENGTH"
                :error="errors.length > 0"
                :error-message="errors[0]"
              />
            </Field>
          </fieldset>
        </QCardSection>
        <QCardActions align="center" class="bg-white q-mb-md">
          <QBtn type="submit" :label="$t('ETAB.PAGES.RECRUITMENT.REPORT_CANDIDATE_MODAL.@CONFIRM')" color="secondary" class="q-px-lg" no-caps />
        </QCardActions>
      </VeeForm>
    </template>
  </ExtracadabraModal>
</template>

<script>
  import { SHORT_TEXT_LENGTH_LIMIT } from "@core/constants";
  import {
    ACTION_REPORT_MODAL_SET,
    ACTION_REPORT_CANDIDATE_SET,
    ACTION_RECRUITMENT_CANDIDATES_GET_REPORT_REASONS,
    ACTION_REPORT_DEMAND_SET,
    GETTER_RECRUITMENT_CANDIDATES_GET_REPORT_REASONS,
    GETTER_REPORT_DEMAND_GET,
    GETTER_REPORT_CANDIDATE_ID_GET,
    GETTER_REPORT_CANDIDATE_FIRSTNAME_GET,
    ACTION_RECRUITMENT_GET_BY_ID,
  } from "@etab/stores/types";
  import { useReportCandidateStore } from "@etab/stores/report-candidate";
  import { useRecruitmentStore } from "@etab/stores/recruitment";
  import { useRecruitmentCandidatesStore } from "@etab/stores/recruitment-candidates";
  import { mapStores } from "pinia";
  import { NotificationsManager } from "@core/managers/notifications.manager";
  import { recruitmentCandidateMixin } from "@etab/mixins";
  import { ExtracadabraModal } from "@core/components/modals";
  import { Form as VeeForm, Field } from "vee-validate";

  export default {
    components: {
      ExtracadabraModal,
      Field,
      VeeForm,
    },
    mixins: [recruitmentCandidateMixin],
    props: {
      open: {
        type: Boolean,
        default: false,
      },
    },
    emits: ["refresh"],
    data() {
      return {
        reasonId: undefined,
        comment: "",
        MAX_LENGTH: SHORT_TEXT_LENGTH_LIMIT,
      };
    },
    computed: {
      ...mapStores(useRecruitmentCandidatesStore, useRecruitmentStore, useReportCandidateStore),
      isCommentMandatory() {
        if (this.reasonId) {
          const selectedOption = this.reportCandidateReasons.find((option) => option.getId() === this.reasonId);
          return selectedOption.getRequiredComment();
        }
        return false;
      },
      candidateId() {
        return this.reportCandidateStore[GETTER_REPORT_CANDIDATE_ID_GET];
      },
      candidateFirstname() {
        return this.reportCandidateStore[GETTER_REPORT_CANDIDATE_FIRSTNAME_GET];
      },
      demandId() {
        return this.reportCandidateStore[GETTER_REPORT_DEMAND_GET];
      },
      reportReasons() {
        return this.recruitmentCandidatesStore[GETTER_RECRUITMENT_CANDIDATES_GET_REPORT_REASONS].length;
      },
      reportCandidateReasons() {
        return this.recruitmentCandidatesStore[GETTER_RECRUITMENT_CANDIDATES_GET_REPORT_REASONS];
      },
      formattedReportCandidateReasons() {
        return this.reportCandidateReasons.map((reason) => {
          return {
            value: reason.getId(),
            label: reason.getLabel(),
          };
        });
      },
      openedInternal: {
        get() {
          return this.open;
        },
        set() {
          this.closeReportModal();
        },
      },
    },
    watch: {
      open() {
        this.reinitForm();
      },
    },
    mounted() {
      this.getReportReasons();
    },
    methods: {
      closeReportModal() {
        this.reportCandidateStore[ACTION_REPORT_MODAL_SET](false);
        this.reportCandidateStore[ACTION_REPORT_CANDIDATE_SET]({ candidateId: null, candidateFirstname: "" });
        this.reportCandidateStore[ACTION_REPORT_DEMAND_SET](null);
        return true;
      },
      getReportReasons() {
        this.recruitmentCandidatesStore[ACTION_RECRUITMENT_CANDIDATES_GET_REPORT_REASONS]();
      },
      reinitForm() {
        this.reasonId = undefined;
        this.comment = "";
      },
      async save() {
        const { comment, reasonId } = this;
        try {
          this.$_startGlobalLoading();
          await this.$_reportCandidate({
            candidateId: this.candidateId,
            demandId: this.demandId,
            reasonId,
            comment,
          });
          // update recruitment detail to take the report into account
          await this.recruitmentStore[ACTION_RECRUITMENT_GET_BY_ID](this.demandId);
        } catch (error) {
          NotificationsManager.error();
          this.$_stopGlobalLoading();
          throw error;
        } finally {
          this.$emit("refresh");
          // this.$_stopGlobalLoading(); // loading should be stopped in the refresh method
          this.closeReportModal();
        }
      },
    },
  };
</script>
